import {
  Box,
  Container,
  Group,
  Button,
  Text,
  Burger,
  Drawer,
  useComputedColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconLogin, IconRocket } from "@tabler/icons-react";
import { ColorSchemeToggle } from "./ColorSchemeToggle";
import { ContactFormModal } from "./ContactFormModal";

export function Navbar() {
  const [opened, { toggle, close }] = useDisclosure(false);
  const [demoOpened, { open: openDemo, close: closeDemo }] =
    useDisclosure(false);
  const computedColorScheme = useComputedColorScheme("light");
  // Named VITE_PORTAL_URL for historical/CI-secret continuity. Points at
  // frontend/apps/portal's own local /login (no more standalone gateway app
  // to redirect through — each app hosts its own sign-in). Default matches
  // frontend's local dev port.
  const portalUrl = import.meta.env.VITE_PORTAL_URL || "http://localhost:5173";

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      close();
    }
  };

  const navLinks = [
    { label: "Features", id: "features" },
    { label: "Pricing", id: "pricing" },
    { label: "FAQ", id: "faq" },
  ];

  return (
    <>
      <Box
        component="nav"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          backgroundColor:
            computedColorScheme === "dark"
              ? "rgba(26, 27, 30, 0.95)"
              : "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderBottom:
            computedColorScheme === "dark"
              ? "1px solid #373A40"
              : "1px solid #e9ecef",
        }}
      >
        <Container size="xl">
          <Group h={70} justify="space-between">
            {/* Logo */}
            <Box
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <img
                src="/assets/livingsync-logo.svg"
                alt="LivingSyncs"
                style={{ height: "40px", width: "auto" }}
              />
            </Box>

            {/* Desktop Navigation */}
            <Group gap="lg" visibleFrom="sm">
              {navLinks.map((link) => (
                <Text
                  key={link.id}
                  size="sm"
                  fw={500}
                  style={{ cursor: "pointer", transition: "color 0.2s" }}
                  c="dimmed"
                  onClick={() => scrollToSection(link.id)}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "#4a8c71")
                  }
                  onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                >
                  {link.label}
                </Text>
              ))}
              <ColorSchemeToggle />
              <Button
                variant="subtle"
                leftSection={<IconLogin size={18} />}
                component="a"
                href={`${portalUrl}/login`}
              >
                Login
              </Button>
              <Button
                leftSection={<IconRocket size={18} />}
                onClick={openDemo}
                styles={{
                  root: {
                    background:
                      "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                  },
                }}
              >
                Request a Demo
              </Button>
            </Group>

            {/* Mobile Menu Button */}
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label="Toggle navigation menu"
              // size="sm" renders a 26px button — this is the only way to reach
              // navigation on mobile, so widen the hit area to the ~44px
              // comfortable tap target while keeping the icon the same size.
              styles={{ root: { width: 44, height: 44 } }}
            />
          </Group>
        </Container>
      </Box>

      {/* Mobile Drawer */}
      <Drawer opened={opened} onClose={close} size="xs" position="right">
        <Box p="md">
          {/* Brand Icon in Drawer */}
          <Box ta="center" mb="xl">
            <img
              src="/assets/livingsync-icon.svg"
              alt="LivingSyncs Icon"
              style={{ height: "48px", width: "auto", margin: "0 auto" }}
            />
            <Text size="sm" c="dimmed" mt="xs" fs="italic">
              Empowering Health, Enriching Lives!
            </Text>
          </Box>

          {navLinks.map((link) => (
            <Text
              key={link.id}
              size="lg"
              fw={500}
              mb="md"
              style={{ cursor: "pointer" }}
              onClick={() => scrollToSection(link.id)}
            >
              {link.label}
            </Text>
          ))}
          <Group mt="xl" mb="md">
            <Text size="sm" fw={500}>
              Theme:
            </Text>
            <ColorSchemeToggle />
          </Group>
          <Button
            fullWidth
            mt="md"
            leftSection={<IconRocket size={18} />}
            onClick={() => {
              close();
              openDemo();
            }}
            styles={{
              root: {
                background: "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
              },
            }}
          >
            Request a Demo
          </Button>
          <Button
            fullWidth
            mt="sm"
            variant="subtle"
            leftSection={<IconLogin size={18} />}
            component="a"
            href={`${portalUrl}/login`}
          >
            Login
          </Button>
        </Box>
      </Drawer>

      <ContactFormModal opened={demoOpened} onClose={closeDemo} />
    </>
  );
}
